import { Helmet } from 'react-helmet';

/**
 * SEO configuration constants
 * @type {Object}
 */
const SEO_CONFIG = {
  siteName: 'EllaFin',
  siteUrl: 'https://www.ellafin.ai',
  defaultTitle: 'EllaFin | Finance-First FinOps Platform',
  defaultDescription:
    'EllaFin is a Finance-First FinOps platform that aligns AWS cloud costs with your internal financial reporting. Built for CFOs, FP&A teams, and FinOps practitioners.',
  defaultImage: 'https://www.ellafin.ai/og-image.png',
  twitterHandle: '', // Add when available
  locale: 'en_US',
};

/**
 * SEO component that manages document head meta tags for each page.
 *
 * Uses react-helmet to inject unique title, description, Open Graph,
 * Twitter Card, and canonical URL meta tags per page. Falls back to
 * site-wide defaults when page-specific values are not provided.
 *
 * @param {Object} props - Component props
 * @param {string} [props.title] - Page title (appends site name automatically)
 * @param {string} [props.description] - Meta description (150-160 chars recommended)
 * @param {string} [props.path] - URL path for canonical and og:url (e.g. '/about')
 * @param {string} [props.ogType] - Open Graph type (default: 'website')
 * @param {string} [props.ogImage] - Open Graph image URL (default: site OG image)
 * @param {string} [props.ogImageAlt] - Alt text for OG image
 * @param {boolean} [props.noIndex] - Whether to add noindex directive
 * @param {string} [props.keywords] - Additional keywords for meta keywords tag
 * @param {Object} [props.structuredData] - JSON-LD structured data object
 * @returns {React.ReactElement} Helmet component with meta tags
 */
function SEO({
  title,
  description,
  path = '',
  ogType = 'website',
  ogImage,
  ogImageAlt = 'EllaFin - Finance-First FinOps Platform',
  noIndex = false,
  keywords,
  structuredData,
}) {
  const fullTitle = title
    ? `${title} | ${SEO_CONFIG.siteName}`
    : SEO_CONFIG.defaultTitle;
  const metaDescription = description || SEO_CONFIG.defaultDescription;
  const canonicalUrl = `${SEO_CONFIG.siteUrl}${path}`;
  const imageUrl = ogImage || SEO_CONFIG.defaultImage;
  const defaultKeywords =
    'FinOps, AWS cost management, cloud cost allocation, finance-first FinOps, CFO cloud costs, FP&A cloud reporting, EllaFin';
  const metaKeywords = keywords
    ? `${defaultKeywords}, ${keywords}`
    : defaultKeywords;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="author" content="EllaFin" />
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SEO_CONFIG.siteName} />
      <meta property="og:locale" content={SEO_CONFIG.locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

export default SEO;
